import { captureFetchError } from './sentry.js'

export const SHOPIFY_GENERIC_ERROR =
  'Something went wrong. Please contact <a href="mailto:support@freeflysystems.com">support@freeflysystems.com</a> <span class="no-wrap">for assistance.</span>'

export const SHOPIFY_GENERIC_ERROR_PLAIN =
  'Something went wrong. Please contact support@freeflysystems.com for assistance.'

type ApiErrorBody = {
  description?: string
  message?: string
  code?: string
}

export class FetchError extends Error {
  readonly status: number
  readonly body?: unknown
  readonly url: string

  constructor(status: number, message?: string, body?: unknown, url = '') {
    super(message ?? `Request failed with status ${status}`)
    this.name = 'FetchError'
    this.status = status
    this.body = body
    this.url = url
  }
}

function getErrorMessage(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined
  const apiBody = body as ApiErrorBody
  return apiBody.description || apiBody.message
}

function isDealerTokenIssueUrl(url: string): boolean {
  return /\/Prod\/issue$/.test(url)
}

function isShopifyProductUrl(url: string): boolean {
  return /\/products\/[^/?]+(\.js|\?view=metafield)/.test(url)
}

function isTransientNetworkError(error: unknown): boolean {
  return (
    error instanceof TypeError
    && /failed to fetch|load failed/i.test(error.message)
  )
}

function shouldReportFetchError(url: string, error: unknown): boolean {
  if (error instanceof FetchError) {
    if (isExpectedCartFetchError(error)) return false
    if (error.status === 429) return false
    if (error.status >= 100 && error.status < 200) return false
    if (
      error.status === 503
      && (isShopifyProductUrl(url) || isDealerTokenIssueUrl(url))
    ) {
      return false
    }
    return true
  }

  if (
    isTransientNetworkError(error)
    && (isShopifyProductUrl(url) || isDealerTokenIssueUrl(url))
  ) {
    return false
  }

  return true
}

function isExpectedCartFetchError(error: FetchError): boolean {
  if (!/\/cart\//.test(error.url)) return false

  if (error.status === 409) return true

  if (error.status !== 400 && error.status !== 422) return false

  const text = [error.message, getErrorMessage(error.body)]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  return (
    text.includes('sold out')
    || text.includes('already in your cart')
    || text.includes('maximum quantity')
    || text.includes('no valid id or line')
    || text.includes('cannot find variant')
  )
}

export async function fetchJson<T = unknown>(
  url: string,
  init?: RequestInit,
): Promise<T> {
  const method = init?.method ?? 'GET'
  let response: Response

  try {
    response = await fetch(url, init)
  } catch (error) {
    if (shouldReportFetchError(url, error)) {
      captureFetchError(error, { url, method })
    }
    throw error
  }

  let body: unknown

  try {
    body = await response.json()
  } catch {
    body = undefined
  }

  if (!response.ok) {
    const error = new FetchError(
      response.status,
      getErrorMessage(body),
      body,
      url,
    )
    if (shouldReportFetchError(url, error)) {
      captureFetchError(error, { url, method })
    }
    throw error
  }

  return body as T
}

export async function fetchOk(url: string, init?: RequestInit): Promise<boolean> {
  try {
    const response = await fetch(url, init)
    return response.ok
  } catch (error) {
    if (shouldReportFetchError(url, error)) {
      captureFetchError(error, { url, method: init?.method ?? 'GET' })
    }
    throw error
  }
}
